import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Comment as CommentIcon,
  Photo as PhotoIcon,
  Timeline as TimelineIcon,
  Close as CloseIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { workOrdersApi } from '../../services/api';
import WorkOrderPhotoUpload from './WorkOrderPhotoUpload';

const ContractorWorkOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  
  const [workOrder, setWorkOrder] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Photo viewer state
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    fetchWorkOrderDetails();
  }, [id]);

  const fetchWorkOrderDetails = async () => {
    try {
      setLoading(true);
      setError('');

      const [detailsResponse, photosResponse, activitiesResponse] = await Promise.all([
        workOrdersApi.getWorkOrderById(id),
        workOrdersApi.getPhotos(id),
        workOrdersApi.getActivities(id)
      ]);

      setWorkOrder(detailsResponse.data);
      setPhotos(photosResponse.data || []);
      setActivities(activitiesResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch work order details:', error);
      setError('Failed to load work order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotosUploaded = (newPhotos) => {
    setPhotos(prev => [...prev, ...newPhotos]);
    setSnackbar({
      open: true,
      message: `${newPhotos.length} photo(s) uploaded successfully`,
      severity: 'success'
    });
  };

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
    setPhotoViewerOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'default';
      case 'assigned': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const canUploadPhotos = () => {
    // Users can upload photos if they are assigned to the work order
    return (
      workOrder &&
      (workOrder.assigned_to_user_id === user.id ||
       (user.contractor_company_id && workOrder.assigned_to_company_id === user.contractor_company_id) ||
       hasRole(['manager', 'admin']))
    );
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '50vh',
        p: 3 
      }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Loading work order details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!workOrder) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Work order not found</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Work Order Details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Work Order Summary */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  {workOrder.work_order_number}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    label={workOrder.status} 
                    color={getStatusColor(workOrder.status)} 
                    size="small" 
                  />
                  <Chip 
                    label={workOrder.priority} 
                    color={getPriorityColor(workOrder.priority)} 
                    size="small" 
                  />
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                {workOrder.title}
              </Typography>

              <Typography color="text.secondary" paragraph>
                {workOrder.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      Location: {workOrder.location || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      Due: {formatDateTime(workOrder.due_date)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      Assigned to: {workOrder.assigned_user_name || 'Not assigned'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      Company: {workOrder.assigned_company_name || 'Not assigned'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<EditIcon />}
                  fullWidth
                  disabled={!canUploadPhotos()}
                >
                  Update Status
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<CommentIcon />}
                  fullWidth
                  disabled={!canUploadPhotos()}
                >
                  Add Notes
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<AssignmentIcon />}
                  fullWidth
                  disabled={!hasRole(['manager', 'admin'])}
                >
                  Reassign
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabs Section */}
        <Grid item xs={12}>
          <Paper>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label={`Photos (${photos.length})`} icon={<PhotoIcon />} />
              <Tab label={`Activities (${activities.length})`} icon={<TimelineIcon />} />
              <Tab label="Upload Photos" icon={<PhotoIcon />} disabled={!canUploadPhotos()} />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* Photos Tab */}
              {activeTab === 0 && (
                <Box>
                  {photos.length === 0 ? (
                    <Alert severity="info">No photos uploaded yet.</Alert>
                  ) : (
                    <ImageList variant="masonry" cols={4} gap={8}>
                      {photos.map((photo) => (
                        <ImageListItem 
                          key={photo.id} 
                          onClick={() => handlePhotoClick(photo)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <img
                            src={`${process.env.REACT_APP_API_URL}${photo.file_path}`}
                            alt={photo.caption || 'Work order photo'}
                            loading="lazy"
                            style={{ borderRadius: 4 }}
                          />
                          <ImageListItemBar
                            title={photo.photo_type}
                            subtitle={photo.caption}
                            actionIcon={
                              <IconButton sx={{ color: 'rgba(255, 255, 255, 0.54)' }}>
                                <DownloadIcon />
                              </IconButton>
                            }
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  )}
                </Box>
              )}

              {/* Activities Tab */}
              {activeTab === 1 && (
                <Box>
                  {activities.length === 0 ? (
                    <Alert severity="info">No activities recorded yet.</Alert>
                  ) : (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Activity</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activities.map((activity) => (
                          <TableRow key={activity.id}>
                            <TableCell>{formatDateTime(activity.created_at)}</TableCell>
                            <TableCell>
                              <Chip label={activity.activity_type} size="small" />
                            </TableCell>
                            <TableCell>{activity.created_by_name}</TableCell>
                            <TableCell>{activity.description}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}

              {/* Photo Upload Tab */}
              {activeTab === 2 && canUploadPhotos() && (
                <WorkOrderPhotoUpload
                  workOrderId={id}
                  onPhotosUploaded={handlePhotosUploaded}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Photo Viewer Modal */}
      <Dialog
        open={photoViewerOpen}
        onClose={() => setPhotoViewerOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedPhoto?.photo_type} - {selectedPhoto?.caption}
            </Typography>
            <IconButton onClick={() => setPhotoViewerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPhoto && (
            <img
              src={`${process.env.REACT_APP_API_URL}${selectedPhoto.file_path}`}
              alt={selectedPhoto.caption || 'Work order photo'}
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractorWorkOrderDetails; 